/**
 * Mapping API — suggestions, recipes, feedback, approvals, regeneration, health, preview.
 */
import axios, { AxiosError } from 'axios';
import { apiClient, buildUrl } from './apiClient';
import { buildEmptyMappingResponse } from './batchApi';
import type {
  MappingResponse,
  FieldMapping,
  ApplyTransformationRuleRequest,
  UpdateMappingRequest,
  CreateMappingRequest,
  TransformationTestResponse,
  ValidatorRunListResponse,
  SchemaDriftEventsResponse,
  MappingHealthResponse,
  TransformationRuleListResponse,
  ExpressionValidationResponse,
  RegenerationContextResponse,
  PromptRegenerateMappingRequest,
  ExecutionRun,
  CompatibilityVerdict,
  ApplyRecipeResponse,
  ProjectRecipeSummary,
} from './types';

// --- Mapping CRUD ---

export async function getMappings(batchId: string): Promise<MappingResponse> {
  try {
    const response = await apiClient.get(buildUrl(`/api/v1/mappings/${batchId}`));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const err = error as AxiosError;
      if (err.response?.status === 404) {
        console.warn(`No mapping recipe found for batch ${batchId}, returning empty response.`);
        return buildEmptyMappingResponse(batchId);
      }
    }
    console.error('Failed to get mappings:', error);
    throw error;
  }
}

export async function generateMappings(
  batchId: string,
  options: {
    targetSchema?: any;
    projectId?: string;
    targetSchemaId?: string | null;
    useHistory?: boolean;
    generationMode?: 'auto' | 'full';
    metadata?: Record<string, any>;
  } = {}
): Promise<MappingResponse> {
  const payload: Record<string, any> = {
    batch_id: batchId,
    client_id: 'web-ui',
    use_history: options.useHistory ?? true,
  };

  if (options.targetSchema) {
    payload.target_schema = options.targetSchema;
  }
  if (options.projectId) {
    payload.project_id = options.projectId;
  }
  if (options.targetSchemaId) {
    payload.target_schema_id = options.targetSchemaId;
  }
  if (options.generationMode) {
    payload.generation_mode = options.generationMode;
  }
  if (options.metadata) {
    payload.metadata = options.metadata;
  }
  const response = await apiClient.post(buildUrl('/api/v1/mappings/suggest'), payload, {
    timeout: 300000
  });
  return response.data;
}

export async function approveMappings(batchId: string, recipeId: string, approvedBy: string): Promise<void> {
  await apiClient.post(buildUrl(`/api/v1/mappings/${batchId}/approve`), {
    recipe_id: recipeId,
    approved_by: approvedBy,
  });
}

export async function applyTransformationRule(batchId: string, payload: ApplyTransformationRuleRequest): Promise<MappingResponse> {
  const body = {
    applied_by: 'web-ui',
    ...payload,
  };
  const response = await apiClient.post(buildUrl(`/api/v1/mappings/${batchId}/rules/apply`), body);
  return response.data;
}

export async function updateMapping(batchId: string, mappingId: string, payload: UpdateMappingRequest): Promise<MappingResponse> {
  const body = {
    edited_by: 'web-ui',
    ...payload,
  };
  const response = await apiClient.patch(buildUrl(`/api/v1/mappings/${batchId}/mappings/${mappingId}`), body);
  return response.data;
}

export async function createMapping(batchId: string, payload: CreateMappingRequest): Promise<FieldMapping> {
  const body = {
    created_by: 'web-ui',
    ...payload,
  };
  const response = await apiClient.post(buildUrl(`/api/v1/mappings/${batchId}/mappings`), body);
  return response.data;
}

export async function deleteMapping(batchId: string, mappingId: string, recipeId: string): Promise<MappingResponse> {
  const response = await apiClient.delete(
    buildUrl(`/api/v1/mappings/${batchId}/mappings/${mappingId}?recipe_id=${encodeURIComponent(recipeId)}`)
  );
  return response.data;
}

// --- Issue #587: validator-pass remediation ---

/**
 * Issue #587: a typed candidate source — the shape the resolve
 * endpoint's ``apply_direct_mapping`` action consumes directly. No UI
 * string-parsing required.
 */
export interface CandidateSource {
  source_file: string;
  source_field: string;
  similarity: number;
}

/**
 * Issue #668 refinement — Mapper's audit trail of source columns it
 * considered and rejected before abstaining. Distinct from
 * CandidateSource (validator-derived name-similarity hits): this is
 * the LLM's own enumeration, enforced at tool-call time so every
 * non-trivial abstention shows the operator "the Mapper looked at
 * X, Y, Z and rejected each because…".
 */
export interface ConsideredSource {
  source_file: string;
  source_field: string;
  rejection_reason: string;
}

export interface PendingDecisionPayload {
  description?: string | null;
  is_nullable?: boolean;
  validator_rules?: Array<{ rule?: string; type?: string; message?: string; [k: string]: any }>;
  candidate_sources?: CandidateSource[];
  considered_sources?: ConsideredSource[];
  // RULE_VIOLATION extras:
  rule?: string;
  declared?: any;
  actual_samples?: any[];
  source_field?: string | null;
  mapping_op?: string;
  [k: string]: any;
}

/**
 * Refinement (Apr 2026): the four operator-decision kinds.
 *
 *  - `ambiguous_candidates`: multiple plausibly-correct sources; operator picks.
 *  - `missing_information`: required input not derivable from any context.
 *  - `schema_policy_conflict`: source data legitimate but conflicts with a
 *    schema rule; operator decides whether to override.
 *  - `source_data_quality`: source data unrecoverable; operator picks a
 *    data-quality policy (drop / sentinel / null / fail load).
 */
export type DecisionKind =
  | 'ambiguous_candidates'
  | 'missing_information'
  | 'schema_policy_conflict'
  | 'source_data_quality'
  | 'mapper_repeated_rejection';

/**
 * Per-round trace of what CorrectionAgent did for this decision's
 * (target_table, target_field). Surfaced in the operator UI's
 * `<CorrectionHistoryAccordion/>` so the operator can see what was
 * tried and why the LLM gave up before deciding how to resolve.
 */
export interface CorrectionAttempt {
  round: number;
  target_table: string;
  target_field: string;
  proposed_mapping?: Record<string, any> | null;
  outcome: 'fixed' | 'still_violating' | 'no_proposal' | 'abstained';
  abstain_reason?: DecisionKind | null;
  rationale?: string | null;
  /**
   * Issue #668 refinement — populated only when `outcome === 'abstained'`
   * and the abstention came through the tool's enforced
   * `considered_sources` field. Empty for non-abstain attempts and for
   * system-synthesized abstentions (escalation auto-abstain, Phase 2.27).
   */
  considered_sources?: ConsideredSource[];
}

export interface PendingDecision {
  code: 'MAPPING_INCOMPLETE' | 'RULE_VIOLATION';
  target_table: string;
  target_field: string;
  severity: 'error' | 'warning' | 'info';
  message?: string;
  payload: PendingDecisionPayload;
  // Refinement (Apr 2026): every persisted decision carries a typed
  // discriminator + audit trail.
  kind: DecisionKind;
  correction_history: CorrectionAttempt[];
  // Set when the operator deferred this decision; bumped each time.
  deferred_count?: number;
  last_deferred_at?: string;
  last_deferred_note?: string;
}

export interface PendingDecisionsResponse {
  batch_id: string;
  count: number;
  items: PendingDecision[];
}

// ---------------------------------------------------------------------------
// PR1 / Workstream A-D — StructuralBlocker
// ---------------------------------------------------------------------------
//
// Distinct from `PendingDecision` (operator-actionable mapping issues)
// and `ValidationWarning` (data-rule issues). A StructuralBlocker
// records a contract violation upstream of mapping (canonical_target /
// assignment / identity) that the LLM cannot repair via the correction
// loop. The mapping pipeline never halts on these — they ride on
// `recipe.metadata.structural_blockers` (and `PromptRunResponse.structural_blockers`).
// Only the assembler refuses based on `severity`:
//   - `block_assembly` (schema-wide)        → assembly raises ASSEMBLY_BLOCKED_BY_STRUCTURAL_CONTRACT
//   - `block_assembly_level` (level-scoped) → that level emits zero rows
//   - `warn`                                → UI signal only
//

export type StructuralBlockerCode =
  | 'VALUE_CATALOG_REF_ORPHAN'
  | 'IDENTITY_COLUMN_NOT_IN_TABLE'
  | 'LEVEL_FIELDS_OVERLAP_IDENTITY'
  | 'ANCESTOR_ELEMENT_UNRESOLVED'
  | 'PARENT_KEY_NOT_IN_PARENT'
  | 'STALE_INTERPRETATION_HASH'
  | 'ASSIGNMENT_TABLE_MISSING'
  | 'ASSIGNMENT_SOURCE_UNKNOWN'
  | 'ASSIGNMENT_RESERVED_PREFIX'
  | 'IDENTITY_CONSTANT_COLUMN_MISSING'
  | 'IDENTITY_CONSTANT_OVERRIDES_REAL_MAPPING'
  | 'IDENTITY_CONSTANT_RESERVED_PREFIX'
  // PR7 / Workstream E — recipe-vs-schema role validation.
  | 'ANCESTOR_IDENTITY_MAPPED_TO_NULL'
  | 'ANCESTOR_IDENTITY_LITERAL_BREAKS_LINEAGE'
  | 'ANCESTOR_IDENTITY_LINEAGE_REPAIRED'
  | 'PRIMARY_KEY_MAPPED_TO_NULL'
  | 'IDENTITY_COLUMN_MAPPED_TO_NULL'
  | 'PARENT_KEY_MAPPED_TO_NULL'
  | 'SOURCE_LINEAGE_EMPTY';

export type StructuralBlockerSeverity =
  | 'block_assembly'
  | 'block_assembly_level'
  | 'warn';

export type StructuralBlockerPhase =
  | 'interpretation'
  | 'phase2a'
  | 'phase15'
  | 'phase225'
  | 'phase226'
  | 'phase227'
  | 'recipe_validation'
  | 'assembly';

export interface StructuralBlocker {
  code: StructuralBlockerCode;
  severity: StructuralBlockerSeverity;
  level_element: string | null;
  target_table: string | null;
  target_field: string | null;
  message: string;
  payload: Record<string, unknown>;
  remediation: string;
  detected_at_phase: StructuralBlockerPhase;
}

// --- Resolve action: discriminated union ----------------------------------
//
// Refinement (Apr 2026): seven action variants, each one a
// `{action: '<name>', ...kind-specific fields}` shape. The backend
// enforces a kind-action allow-list:
//
//   ambiguous_candidates  → pick_candidate / provide_expression / defer / dismiss
//   missing_information   → provide_constant / provide_expression /
//                           apply_policy / defer / dismiss
//   schema_policy_conflict → accept_override / pick_candidate /
//                            apply_policy / defer / dismiss
//   source_data_quality   → apply_policy / provide_expression /
//                           defer / dismiss
//
// Sending a flat-string legacy action name yields 422.

export interface PickCandidateAction {
  action: 'pick_candidate';
  /** Zero-based index into `decision.payload.candidate_sources`. */
  candidate_index: number;
}

export interface ProvideConstantAction {
  action: 'provide_constant';
  value: string | number | boolean | null;
}

export interface ProvideExpressionAction {
  action: 'provide_expression';
  expression: string;
}

export type PolicyChoice =
  | 'use_sentinel'
  | 'drop_rows'
  | 'set_null_with_reason'
  | 'fail_load';

export interface ApplyPolicyAction {
  action: 'apply_policy';
  policy: PolicyChoice;
  /** Required when policy === 'use_sentinel'. */
  sentinel_value?: string | number | boolean | null;
  /** Required when policy === 'set_null_with_reason'. */
  null_reason?: string;
}

export interface AcceptOverrideAction {
  action: 'accept_override';
  /** Required audit text — the operator's WHY. */
  rationale: string;
}

export interface DeferAction {
  action: 'defer';
  note?: string;
}

export interface DismissAction {
  action: 'dismiss';
  /** Required audit text — the operator's WHY. */
  rationale: string;
}

export type ResolveAction =
  | PickCandidateAction
  | ProvideConstantAction
  | ProvideExpressionAction
  | ApplyPolicyAction
  | AcceptOverrideAction
  | DeferAction
  | DismissAction;

/**
 * Allow-list mirror of the backend `ALLOWED_ACTIONS_BY_KIND` table.
 * The drawer uses this to gate which action UIs to render per kind.
 */
export const ALLOWED_ACTIONS_BY_KIND: Record<DecisionKind, ResolveAction['action'][]> = {
  ambiguous_candidates: ['pick_candidate', 'provide_expression', 'defer', 'dismiss'],
  missing_information: [
    'provide_constant', 'provide_expression', 'apply_policy', 'defer', 'dismiss',
  ],
  schema_policy_conflict: [
    'accept_override', 'pick_candidate', 'apply_policy', 'defer', 'dismiss',
  ],
  source_data_quality: ['apply_policy', 'provide_expression', 'defer', 'dismiss'],
  // ``mapper_repeated_rejection`` is the auto-synthesized abstention
  // the Mapper emits when its proposals fail validation across the
  // rejection threshold. It is NOT a "missing data" case — it is a
  // Mapper-quality case. Action surface mirrors ``missing_information``
  // (the operator can supply a value, expression, or policy) plus
  // ``pick_candidate`` (the original proposed mapping is often close
  // and an alternative source may resolve it).
  mapper_repeated_rejection: [
    'pick_candidate', 'provide_constant', 'provide_expression',
    'apply_policy', 'defer', 'dismiss',
  ],
};

export interface ResolvePendingDecisionRequest {
  target_table: string;
  target_field: string;
  action: ResolveAction;
  resolved_by?: string;
}

export interface ResolvePendingDecisionResponse {
  batch_id: string;
  target_table: string;
  target_field: string;
  action: ResolveAction['action'];
  decision_kind: DecisionKind;
  mapping_id: string | null;
  remaining_decisions: number;
}

export async function getPendingDecisions(
  batchId: string,
): Promise<PendingDecisionsResponse> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/mappings/${batchId}/pending-decisions`),
  );
  return response.data;
}

export async function resolvePendingDecision(
  batchId: string,
  request: ResolvePendingDecisionRequest,
  idempotencyKey?: string,
): Promise<ResolvePendingDecisionResponse> {
  // Issue #672 Pocket 4 — Idempotency-Key is required by the server.
  // Caller may supply one explicitly (to retry the same logical op);
  // otherwise we mint a fresh UUID per call (each invocation is a
  // distinct logical operation by default).
  const key =
    idempotencyKey ??
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const body = { resolved_by: 'web-ui', ...request };
  const response = await apiClient.post(
    buildUrl(`/api/v1/mappings/${batchId}/pending-decisions/resolve`),
    body,
    { headers: { 'Idempotency-Key': key } },
  );
  return response.data;
}

// --- Approval ---

export async function approveMapping(
  batchId: string,
  mappingId: string,
  approvedBy: string = 'web-ui'
): Promise<MappingResponse> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/mappings/${batchId}/mappings/${mappingId}/approve`),
    { approved_by: approvedBy }
  );
  return response.data;
}

export async function approveTable(
  batchId: string,
  tableName: string,
  recipeId: string,
  approvedBy: string = 'web-ui'
): Promise<MappingResponse> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/mappings/${batchId}/tables/${encodeURIComponent(tableName)}/approve`),
    { recipe_id: recipeId, approved_by: approvedBy }
  );
  return response.data;
}

// --- Regeneration ---

export async function promptRegenerateMapping(
  batchId: string,
  mappingId: string,
  payload: PromptRegenerateMappingRequest
): Promise<MappingResponse> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/mappings/${batchId}/mappings/${mappingId}/prompt-regenerate`),
    {
      recipe_id: payload.recipeId,
      instructions: payload.instructions,
      explanation: payload.explanation ?? null,
      actor: payload.actor ?? 'web-ui',
      provider: payload.provider ?? null,
    }
  );
  return response.data;
}

export async function getRegenerationContext(
  batchId: string,
  mappingId: string,
  recipeId?: string
): Promise<RegenerationContextResponse> {
  const params = new URLSearchParams();
  if (recipeId) {
    params.append('recipe_id', recipeId);
  }
  const query = params.toString();
  const response = await apiClient.get<RegenerationContextResponse>(
    buildUrl(
      `/api/v1/mappings/${batchId}/mappings/${mappingId}/regeneration-context${query ? `?${query}` : ''}`
    )
  );
  return response.data;
}

// --- Preview ---

export async function getTransformPreview(
  batchId: string,
  recipeId: string,
  limit: number = 100,
  targetTables?: string[]
): Promise<TransformationTestResponse> {
  const payload: Record<string, unknown> = {
    recipe_id: recipeId,
    sample_size: limit,
  };
  if (Array.isArray(targetTables) && targetTables.length > 0) {
    payload.target_tables = targetTables;
  }
  const response = await apiClient.post(
    buildUrl(`/api/v1/mappings/${batchId}/transformations/test`),
    payload
  );
  return response.data;
}

export async function getDistributedPreview(
  batchId: string,
  recipeId: string,
  limit: number = 100,
  targetTables?: string[]
): Promise<TransformationTestResponse> {
  const payload: Record<string, unknown> = {
    recipe_id: recipeId,
    sample_size: limit,
  };
  if (Array.isArray(targetTables) && targetTables.length > 0) {
    payload.target_tables = targetTables;
  }
  const response = await apiClient.post(
    buildUrl(`/api/v1/mappings/${batchId}/preview/distributed`),
    payload
  );
  return response.data;
}

// --- Feedback ---

export async function submitMappingFeedback(
  batchId: string,
  mappingId: string,
  payload: {
    recipe_id: string;
    feedback_type: 'question' | 'regenerate_request';
    message: string;
    actor?: string;
  }
): Promise<{ status: string; llm_response?: string; updated_mapping?: Record<string, unknown>; metadata?: Record<string, unknown> }> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/batches/${batchId}/mappings/${mappingId}/feedback`),
    { ...payload, actor: payload.actor ?? 'web-ui' }
  );
  return response.data;
}

export async function submitSuggestionFeedback(
  batchId: string,
  suggestionId: string,
  payload: { recipe_id: string; status: 'accepted' | 'rejected'; user: string; reason?: string }
): Promise<void> {
  await apiClient.post(buildUrl(`/api/v1/mappings/${batchId}/suggestions/${suggestionId}/feedback`), payload);
}

// --- Telemetry & Health ---

export async function getValidatorTelemetry(
  batchId: string,
  options: { recipeId?: string; stage?: string; limit?: number } = {}
): Promise<ValidatorRunListResponse> {
  const params = new URLSearchParams();
  if (options.recipeId) {
    params.append('recipe_id', options.recipeId);
  }
  if (options.stage) {
    params.append('stage', options.stage);
  }
  if (options.limit) {
    params.append('limit', String(options.limit));
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await apiClient.get(buildUrl(`/api/v1/mappings/${batchId}/telemetry${query}`));
  return response.data;
}

export async function getSchemaDrift(
  batchId: string,
  options: { clientId?: string; schemaName?: string; limit?: number } = {}
): Promise<SchemaDriftEventsResponse> {
  const params = new URLSearchParams();
  if (options.clientId) {
    params.append('client_id', options.clientId);
  }
  if (options.schemaName) {
    params.append('schema_name', options.schemaName);
  }
  if (options.limit) {
    params.append('limit', String(options.limit));
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await apiClient.get(buildUrl(`/api/v1/mappings/${batchId}/schema-drift${query}`));
  return response.data;
}

export async function getMappingHealth(batchId: string): Promise<MappingHealthResponse> {
  const response = await apiClient.get(buildUrl(`/api/v1/mappings/${batchId}/health`));
  return response.data;
}

export async function listTransformationRules(category?: string): Promise<TransformationRuleListResponse> {
  const params = new URLSearchParams();
  if (category) {
    params.append('category', category);
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await apiClient.get(buildUrl(`/api/v1/transformation-rules${query}`));
  return response.data;
}

// --- Expression validation ---

export async function validateExpression(
  expression: string,
  sourceColumns: string[],
  sourceFile?: string
): Promise<ExpressionValidationResponse> {
  const response = await apiClient.post(
    buildUrl('/api/v1/mappings/validate-expression'),
    { expression, source_columns: sourceColumns, source_file: sourceFile }
  );
  return response.data;
}

// =====================================================================
// Recipe-Scoped APIs (Recipe-Batch Decoupling)
// =====================================================================

export async function listApprovedRecipes(
  options: {
    targetSchemaId?: string;
    excludeProjectId?: string;
    tenantId?: string;
    limit?: number;
  } = {}
): Promise<ProjectRecipeSummary[]> {
  const params = new URLSearchParams();
  if (options.targetSchemaId) params.append('target_schema_id', options.targetSchemaId);
  if (options.excludeProjectId) params.append('exclude_project_id', options.excludeProjectId);
  if (options.tenantId) params.append('tenant_id', options.tenantId);
  if (options.limit) params.append('limit', String(options.limit));
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await apiClient.get(buildUrl(`/api/v1/recipes/approved${query}`));
  return response.data?.recipes || [];
}

export async function getProjectRecipe(
  projectId: string
): Promise<ProjectRecipeSummary | null> {
  try {
    const response = await apiClient.get(buildUrl(`/api/v1/projects/${projectId}/recipe`));
    return response.data?.recipe || null;
  } catch (error) {
    if (axios.isAxiosError(error) && (error as AxiosError).response?.status === 404) {
      return null; // No recipe for this project — expected case
    }
    throw error;
  }
}

export async function getProjectRecipes(
  projectId: string
): Promise<ProjectRecipeSummary[]> {
  const response = await apiClient.get(buildUrl(`/api/v1/projects/${projectId}/recipes`));
  return response.data?.recipes || [];
}

export async function activateRecipe(recipeId: string): Promise<void> {
  await apiClient.post(buildUrl(`/api/v1/recipes/${recipeId}/activate`));
}

export async function checkSchemaCompatibility(
  recipeId: string,
  batchId: string
): Promise<CompatibilityVerdict> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/recipes/${recipeId}/check-compatibility/${batchId}`)
  );
  return response.data;
}

export async function applyRecipe(
  recipeId: string,
  batchId: string
): Promise<ApplyRecipeResponse> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/recipes/${recipeId}/apply/${batchId}`),
    {},
    { timeout: 300000 }
  );
  return response.data;
}

export async function loadRecipeData(
  recipeId: string,
  batchId: string
): Promise<ExecutionRun> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/recipes/${recipeId}/load/${batchId}`),
    {},
    { timeout: 300000 }
  );
  return response.data;
}

export async function getProjectExecutions(
  projectId: string,
  limit: number = 20
): Promise<ExecutionRun[]> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/projects/${projectId}/executions?limit=${limit}`)
  );
  return response.data?.executions || [];
}

export async function getRecipeExecutions(
  recipeId: string,
  limit: number = 20
): Promise<ExecutionRun[]> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/recipes/${recipeId}/executions?limit=${limit}`)
  );
  return response.data?.executions || [];
}

export async function getExecutionRun(runId: string): Promise<ExecutionRun> {
  const response = await apiClient.get(buildUrl(`/api/v1/execution-runs/${runId}`));
  return response.data;
}

export async function deleteBatch(batchId: string): Promise<void> {
  await apiClient.delete(buildUrl(`/api/v1/batches/${batchId}`));
}

export async function retryExecution(runId: string): Promise<ExecutionRun> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/execution-runs/${runId}/retry`),
    {},
    { timeout: 300000 }
  );
  return response.data;
}
